/**
 * `status` — an edge-to-edge colour fill of the 36 px cell, label centred in white
 * (M2 §5.2, §6; measured on `[ui:library/table-04-column-header-menu.png]`).
 *
 * The `index` trap is `packages/shared`'s: `value.index` is the **label id**, and the display
 * order comes from `labels_positions_v2`, never from the id `[php§3.2]`, `[php§4.3]`.
 */
import {
  findLabelById,
  findLabelIdByText,
  getColumnType,
  isDoneLabel,
  labelStyle,
  orderedLabelIds,
  type StatusSettings,
  type StatusValue,
} from '@elizmonday/shared';
import { EMPTY_STATUS_COLOR, Icon, icons, statusColor } from '@elizmonday/ui';
import type { BoardColumn, SummaryValue } from '../../data/types';
import { useState } from 'react';
import {
  CellPopover,
  PICKER_SEARCH_THRESHOLD,
  PickerRow,
  PickerSearch,
  PickerSeparator,
} from '../primitives';
import { openColumnSettings } from '../settings-request';
import { StatusSettingsDialog } from './SettingsDialog';
import { defineColumnUi } from '../types';
import { sharedBuckets } from '../buckets';

const shared = getColumnType('status');

/**
 * Measured on monday: `.status-picker-wrapper-v2` is **200 px** wide with a 24 px list inset,
 * which is what makes each option 152 px `[discovery/ui-reference/editors/status/editor.json]`.
 */
export const STATUS_PICKER_WIDTH = 200;

/** The live hex of a label, or the `explosive` grey monday paints an empty status `[B5§8.1]`. */
export function labelHex(settings: StatusSettings, id: number | null): string {
  if (id === null) return statusColor(EMPTY_STATUS_COLOR);
  return labelStyle(settings, id)?.color ?? statusColor(EMPTY_STATUS_COLOR);
}

/**
 * The group-summary **battery** (parity card P-027).
 *
 * A status column's footer — and, since P-030, a collapsed group's summary strip — is a
 * segmented bar, one segment per label, each segment as wide as its share of the group. It is
 * the type's own rendering, which is why it lives here rather than in the footer (rule 6): only
 * `status` knows a label's colour and its display order (`labels_positions_v2`, never the id).
 *
 * Measured on `discovery/ui-reference/parity/90397660/group-collapsed-monday.png`: inside the
 * 140 px cell (x 1189) the bar runs x 1197 -> 1320 and y 235 -> 258, i.e. an 8 px inset either
 * side and 24 px tall, with square ends. A group where no item carries the status is one full
 * `#c4c4c4` segment — monday's empty-status grey — which is exactly what the blank bucket is.
 *
 * `summarySql` counts by **label id**; the `LEFT JOIN` pads a row with no cell into a bucket
 * whose key is the empty string, so that bucket is the empty one and sorts last.
 */
function StatusSummary({ summary, column }: { summary: SummaryValue; column: BoardColumn }) {
  const buckets = (summary.detail as { buckets?: Record<string, number> } | undefined)?.buckets;
  if (!buckets) return <span className="ez-footer__value">{summary.text}</span>;
  const settings = column.settings as StatusSettings;
  const total = Object.values(buckets).reduce((sum, count) => sum + count, 0);
  // A battery with nothing in it is still a battery: monday paints the whole bar `explosive`
  // grey, which is what the container's own background does with no segment over it.
  if (total === 0) return <span className="ez-summary-battery" data-testid={`summary-battery-${column.id}`} />;

  const order = orderedLabelIds(settings).map(String);
  const segments = Object.entries(buckets)
    .filter(([, count]) => count > 0)
    .sort(([a], [b]) => {
      // The blank bucket is not a label and has no position; monday paints it last.
      const left = a === '' ? Number.MAX_SAFE_INTEGER : order.indexOf(a);
      const right = b === '' ? Number.MAX_SAFE_INTEGER : order.indexOf(b);
      return (left < 0 ? order.length : left) - (right < 0 ? order.length : right);
    });

  return (
    <span className="ez-summary-battery" data-testid={`summary-battery-${column.id}`}>
      {segments.map(([key, count]) => {
        const id = key === '' ? null : Number(key);
        return (
          <span
            key={key || 'blank'}
            className="ez-summary-battery__segment"
            data-label={key || 'blank'}
            style={{
              width: `${((count / total) * 100).toFixed(4)}%`,
              backgroundColor: labelHex(settings, id === null || Number.isNaN(id) ? null : id),
            }}
            title={`${
              id === null ? 'Empty' : (findLabelById(settings, id) ?? `#${String(id)}`)
            }: ${String(count)}`}
          />
        );
      })}
    </span>
  );
}

export const statusColumnUi = defineColumnUi<StatusValue, StatusSettings>({
  type: 'status',
  icon: 'columnStatus',
  gallery: { group: 'essentials', keywords: ['status', 'label', 'colour', 'color', 'state'] },
  /** P-022: every `width: null` status column on this account renders **140** px. */
  defaultWidth: 140,
  align: 'center',
  groupable: true,
  /** P-263: the value is item-independent, so it may be written to a whole selection. */
  massEditable: true,

  Summary: StatusSummary,

  Cell: ({ value, settings }) => {
    const id = value?.index ?? null;
    const label = id === null ? '' : (findLabelById(settings, id) ?? '');
    return (
      <span
        className="ez-status-cell"
        style={{ backgroundColor: labelHex(settings, id) }}
        data-done={id !== null && isDoneLabel(settings, id) ? '' : undefined}
        title={label}
      >
        {label}
        {/*
         * The folded-corner marker monday paints at the top-right of a status cell
         * (parity card `P-003`). CSS draws it; it only shows on the focused cell.
         */}
        <span className="ez-status-cell__fold" aria-hidden="true" />
      </span>
    );
  },

  /**
   * The label picker (parity card `P-003`).
   *
   * monday shows the labels **immediately**: a vertical stack of full-width coloured buttons
   * (184 x 40, 8 px apart), then `✎ Edit Labels`, then a separator and the AI row. A search
   * field appears only once the list is long — `PICKER_SEARCH_THRESHOLD`, ASSUMED. The old
   * editor opened on a "Search for labels" input, which is what the user reported.
   */
  Editor: ({ value, settings, initialInput, onCommit, onCancel, column }) => {
    const all = orderedLabelIds(settings);
    const searchable = all.length > PICKER_SEARCH_THRESHOLD || (initialInput ?? '') !== '';
    const [query, setQuery] = useState(initialInput ?? '');
    const ids = all.filter((id) => {
      if (!searchable || query.trim() === '') return true;
      return (findLabelById(settings, id) ?? '').toLowerCase().includes(query.trim().toLowerCase());
    });
    const current = value?.index ?? null;
    return (
      <CellPopover label="Pick a label" width={STATUS_PICKER_WIDTH} onDismiss={onCancel}>
        {searchable ? (
          <PickerSearch value={query} onChange={setQuery} placeholder="Search labels" />
        ) : null}
        <ul className="ez-status-picker" role="listbox" aria-label="Labels">
          {ids.map((id) => (
            <li key={id}>
              <button
                type="button"
                role="option"
                aria-selected={id === current}
                className="ez-status-picker__option"
                style={{ backgroundColor: labelHex(settings, id) }}
                data-testid={`status-option-${String(id)}`}
                onClick={() => onCommit({ index: id })}
              >
                {findLabelById(settings, id)}
              </button>
            </li>
          ))}
          {/* Clearing is the empty label: monday paints the same grey `explosive` swatch. */}
          <li>
            <button
              type="button"
              role="option"
              aria-selected={current === null}
              className="ez-status-picker__option ez-status-picker__option--clear"
              style={{ backgroundColor: labelHex(settings, null) }}
              data-testid="status-option-clear"
              onClick={() => onCommit(null)}
            >
              Clear
            </button>
          </li>
        </ul>

        <PickerRow
          icon={<Icon icon={icons.edit} size={20} ignoreFocusStyle />}
          label="Edit Labels"
          testId="status-edit-labels"
          centered
          onClick={() => openColumnSettings(column.id)}
        />
        <PickerSeparator />
        {/*
         * monday's `✦ Auto-assign labels` is an AI feature and this product ships none
         * (`specs/M1-B1-workspaces-and-boards.md` §3.2). The row is kept so the popover has
         * monday's height and order, and disabled with the reason. Open question raised.
         */}
        <PickerRow
          icon={<Icon icon={icons.aiSparkle} size={16} ignoreFocusStyle />}
          label="Auto-assign labels"
          disabled
          title="AI features are not part of this product"
          testId="status-auto-assign"
        />
      </CellPopover>
    );
  },

  SettingsDialog: StatusSettingsDialog,

  fromClipboard(text, settings) {
    const trimmed = text.trim();
    if (trimmed === '') return null;
    const id = findLabelIdByText(settings, trimmed);
    if (id === undefined) throw new Error(`"${trimmed}" is not a label`);
    return { index: id };
  },
  toClipboard(value, settings) {
    return shared.toText(value, settings) ?? '';
  },
  bucketsOf: sharedBuckets('status'),

  summarise(aggregation, cells, settings) {
    // The battery: one segment per label id, done segments taken from `done_colors` `[php§4.4]`.
    if (aggregation !== 'battery') return null;
    const counts = new Map<number, number>();
    let empty = 0;
    for (const cell of cells) {
      if (!cell) {
        empty += 1;
        continue;
      }
      counts.set(cell.index, (counts.get(cell.index) ?? 0) + 1);
    }
    const filled = cells.length - empty;
    const segments = orderedLabelIds(settings)
      .filter((id) => counts.has(id))
      .map((id) => ({
        id,
        label: findLabelById(settings, id) ?? `#${id}`,
        color: labelHex(settings, id),
        count: counts.get(id) ?? 0,
        done: isDoneLabel(settings, id),
        share: filled === 0 ? 0 : (counts.get(id) ?? 0) / filled,
      }));
    const done = segments.filter((segment) => segment.done).reduce((sum, s) => sum + s.count, 0);
    return {
      aggregation,
      text: filled === 0 ? '' : `${Math.round((done / filled) * 100)}%`,
      detail: { segments, empty },
    };
  },
});
